import { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, Play, RefreshCw, Mail, CheckCircle2, AlertTriangle, XCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume } from './JobPollingPanel';

const POLL_INTERVAL_MS = 2000;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function StatusPill({ status }) {
  if (status === 'ok') {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1">
        <CheckCircle2 className="h-3 w-3" />ok
      </Badge>
    );
  }
  if (status === 'degraded') {
    return (
      <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400 gap-1">
        <AlertTriangle className="h-3 w-3" />degraded
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <XCircle className="h-3 w-3" />broken
    </Badge>
  );
}

const CENSUS_TALLY_LABELS = [
  ['created', 'Ingested'],
  ['rejected_deterministic', 'Deterministic reject'],
  ['rejected_llm', 'Not an invoice (AI)'],
  ['rejected_fetch', 'Fetch failed'],
  ['pending_rate_limited', 'Rate limited'],
  ['not_yet_processed', 'Not yet processed'],
  ['error', 'Error'],
];

export function MailboxIngestionCoveragePanel() {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [probeSender, setProbeSender] = useState('');
  const [probeResult, setProbeResult] = useState(null);
  const [probing, setProbing] = useState(false);

  const [newSenders, setNewSenders] = useState('');
  const [previewResults, setPreviewResults] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);

  const [chain, setChain] = useState(null);
  const [progress, setProgress] = useState(null);
  const [startingCensus, setStartingCensus] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await diagnosticsAPI.mailboxIngestionCoverageSummary();
      setSummary(data);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to load Mailbox Ingestion Coverage.');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.mailboxIngestionCoverageCensusStatus();
      setChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const poll = useJobPolling({
    fetchStatus: diagnosticsAPI.mailboxIngestionCoverageCensusJobStatus,
    onCompleted: useCallback((result) => {
      setProgress(null);
      fetchChain();
      toast.success(result?.truncated ? 'Census run complete -- chain continuing' : 'Census complete');
    }, [fetchChain]),
    onFailed: useCallback((error) => {
      setProgress(null);
      fetchChain();
      toast.error(error || 'Census run failed');
    }, [fetchChain]),
    onProgress: useCallback((p) => setProgress(p), []),
  });

  useJobResume({
    fetchLatest: useCallback(() => diagnosticsAPI.mailboxIngestionCoverageCensusJobLatest(), []),
    poll,
  });

  useEffect(() => {
    fetchSummary();
    fetchChain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chain?.status !== 'running') return undefined;
    const id = setInterval(fetchChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chain?.status, fetchChain]);

  const runProbe = async () => {
    const sender = probeSender.trim();
    if (!sender) {
      toast.error('Enter a sender address first.');
      return;
    }
    setProbing(true);
    setProbeResult(null);
    try {
      const { data } = await diagnosticsAPI.mailboxIngestionCoverageProbe(sender);
      setProbeResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Probe failed.');
    } finally {
      setProbing(false);
    }
  };

  const runPreview = async () => {
    const senders = newSenders.split(',').map((s) => s.trim()).filter(Boolean);
    if (senders.length === 0) {
      toast.error('Enter at least one sender to preview.');
      return;
    }
    setPreviewing(true);
    setPreviewResults(null);
    try {
      const { data } = await diagnosticsAPI.mailboxIngestionScopeConfigPreview(senders);
      setPreviewResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    const senders = newSenders.split(',').map((s) => s.trim()).filter(Boolean);
    if (senders.length === 0) {
      toast.error('Enter at least one sender to add first.');
      return;
    }
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.mailboxIngestionScopeConfigApply(senders);
      toast.success(
        `${data.newly_added.length} sender(s) added, ${data.backfills_triggered.length} backfill(s) triggered`,
      );
      setNewSenders('');
      setPreviewResults(null);
      fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const removeSender = async (sender) => {
    if (!window.confirm(`Remove ${sender} from ingestion scope? Already-ingested mail is unaffected.`)) return;
    try {
      await diagnosticsAPI.mailboxIngestionScopeConfigRemove(sender);
      toast.success(`${sender} removed from scope`);
      fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Remove failed.');
    }
  };

  const runCensus = async () => {
    setStartingCensus(true);
    try {
      const { data } = await diagnosticsAPI.mailboxIngestionCoverageCensusStart();
      if (data.job_id) poll.start(data.job_id);
      else if (data.status === 'chain_running') toast.error('A census is already running.');
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to start census.');
    } finally {
      setStartingCensus(false);
    }
  };

  const running = chain?.status === 'running';
  const live = progress || chain?.progress || chain?.seed;
  const populationEstimate = chain?.metadata?.population_estimate;
  const processed = live?.cohort_processed;
  const totals = live?.totals || {};
  const perSender = live?.per_sender || {};
  const senderRows = Object.entries(perSender).sort((a, b) => (b[1].seen || 0) - (a[1].seen || 0));

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Mailbox Ingestion Coverage</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchSummary} disabled={loadingSummary} data-testid="mic-refresh">
            {loadingSummary ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Every connected mailbox, the live Gmail selection query, a per-sender funnel of what
          gets fetched vs. classified vs. ingested, and operator control over which extra senders
          are in scope. A new sender goes through the exact same dedup/quarantine/review pipeline
          as organic mail.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Account health */}
        <div className="space-y-2" data-testid="mic-account-health">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase">Account health</h4>
          <div className="grid gap-2 sm:grid-cols-3">
            {(summary?.account_health || []).map((acct) => (
              <div key={acct.account_id} className="border rounded-lg p-2 space-y-1" data-testid={`mic-account-${acct.account_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono">{acct.account_id}</span>
                  <StatusPill status={acct.status} />
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{acct.email}</div>
                {acct.action_required && acct.action_text && (
                  <div className="text-[11px] text-red-600 dark:text-red-400">{acct.action_text}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Selection query */}
        {summary?.selection_query && (
          <div className="space-y-1">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase">Live selection query</h4>
            <pre
              className="text-[11px] font-mono bg-muted/40 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all"
              data-testid="mic-selection-query"
            >
              {summary.selection_query}
            </pre>
          </div>
        )}

        {/* Sender probe */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase">Sender probe</h4>
          <div className="flex items-end gap-2">
            <Input
              placeholder="billing@apify.com"
              value={probeSender}
              onChange={(e) => setProbeSender(e.target.value)}
              data-testid="mic-probe-input"
              className="h-8 w-64"
            />
            <Button size="sm" onClick={runProbe} disabled={probing} data-testid="mic-probe-run">
              {probing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Probe
            </Button>
          </div>
          {probeResult && (
            <div className="text-xs space-y-1 border rounded-lg p-2" data-testid="mic-probe-result">
              <div className="font-medium">
                Verdict: <span className="font-mono">{probeResult.verdict}</span>
              </div>
              <div className="text-muted-foreground">
                {fmtInt(probeResult.total_raw_message_count)} in mailbox &middot;{' '}
                {fmtInt(probeResult.total_selected_count)} matched the selection query
                {probeResult.any_account_unavailable && (
                  <span className="text-amber-600 dark:text-amber-400"> &middot; one or more accounts unavailable</span>
                )}
              </div>
              {Object.keys(probeResult.outcome_counts_sampled || {}).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(probeResult.outcome_counts_sampled).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scope config */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase">Ingestion scope</h4>
          {(summary?.scope_config?.include_senders || []).length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="mic-scope-current">
              {summary.scope_config.include_senders.map((s) => (
                <Badge key={s} variant="secondary" className="text-[10px] gap-1">
                  {s}
                  <button type="button" onClick={() => removeSender(s)} data-testid={`mic-scope-remove-${s}`}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <Input
              placeholder="sender1@example.com, sender2@example.com"
              value={newSenders}
              onChange={(e) => setNewSenders(e.target.value)}
              data-testid="mic-scope-add-input"
              className="h-8 w-80"
            />
            <Button size="sm" variant="outline" onClick={runPreview} disabled={previewing} data-testid="mic-scope-preview">
              {previewing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview
            </Button>
            <Button size="sm" onClick={runApply} disabled={applying} data-testid="mic-scope-apply">
              {applying ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply
            </Button>
          </div>
          {previewResults && (
            <div className="text-xs space-y-1" data-testid="mic-scope-preview-result">
              {previewResults.results.map((r) => (
                <div key={r.sender} className="border rounded p-2">
                  <span className="font-mono">{r.sender}</span>: would pull in{' '}
                  <strong>{fmtInt(r.would_pull_in_count)}</strong> message(s) (
                  {fmtInt(r.already_seen_in_mailbox)} already visible in the mailbox)
                  {r.sample_subjects.length > 0 && (
                    <div className="text-muted-foreground mt-0.5">
                      e.g. {r.sample_subjects.slice(0, 3).join(' · ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Census */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase">Per-sender census</h4>
          </div>
          <Button
            size="sm" variant="outline" onClick={runCensus}
            disabled={startingCensus || poll.polling || running}
            data-testid="mic-census-run"
          >
            {(startingCensus || poll.polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
            {running
              ? `Running, ${fmtInt(processed)}/${fmtInt(populationEstimate)}`
              : 'Run Census'}
          </Button>

          {chain && (
            <div className="text-xs space-y-1" data-testid="mic-census-summary">
              <div className="flex items-center gap-1 flex-wrap">
                <Badge variant={running ? 'default' : (chain.status === 'error' || chain.status === 'dead_letter') ? 'destructive' : 'outline'}>
                  {chain.status}
                </Badge>
                {populationEstimate != null && (
                  <span className="text-muted-foreground">
                    {fmtInt(processed)} of ~{fmtInt(populationEstimate)} scanned
                  </span>
                )}
              </div>
              {Object.keys(totals).length > 0 && (
                <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="mic-census-totals">
                  {CENSUS_TALLY_LABELS.map(([key, label]) => {
                    const count = totals[key];
                    if (!count) return null;
                    return <span key={key}>{label}: <strong>{count}</strong></span>;
                  })}
                </div>
              )}
              {senderRows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="text-xs border-collapse w-full" data-testid="mic-census-table">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="pr-3 pb-1">Sender</th>
                        <th className="pr-3 pb-1 text-right">Seen</th>
                        <th className="pr-3 pb-1 text-right">Created</th>
                        <th className="pr-3 pb-1 text-right">Rejected</th>
                        <th className="pr-3 pb-1 text-right">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {senderRows.map(([sender, entry]) => (
                        <tr key={sender} className="border-t" data-testid={`mic-census-row-${sender}`}>
                          <td className="pr-3 py-1 max-w-[16rem] truncate" title={sender}>{sender}</td>
                          <td className="pr-3 py-1 text-right tabular-nums">{entry.seen || 0}</td>
                          <td className="pr-3 py-1 text-right tabular-nums">{entry.created || 0}</td>
                          <td className="pr-3 py-1 text-right tabular-nums">
                            {(entry.rejected_deterministic || 0) + (entry.rejected_llm || 0) + (entry.rejected_fetch || 0)}
                          </td>
                          <td className="pr-3 py-1 text-right tabular-nums">
                            {(entry.pending_rate_limited || 0) + (entry.not_yet_processed || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
